<!--
  <lm-dropdown></lm-dropdown>
  PROPS:
  items: array of objects with properties
  item.label: required
  item.user: special attr for render UserInfo component
  item.customClass
  item.icon: material design icon
  item.iconOutline: material icon outline
  item.checkbox: item is a checkbox
  item.title: item is a title (no selectable)
  item.selected: required if is checkbox type.
  id: id for dynamic composition
  icon: material design icon
  iconOutline: material icon outlined
  iconColor: white | black (default)
  highlightIcon: force icon highlight
  selectedItem: item from items to mark option with class 'is-selected'
  autoClose: close dropdown after item selection
  EVENTS:
  open: Emitted when dropdown opens
  select: Emitted when an item is selected from the menu. Returns the selected item
  close: Emitted when dropdown closes
  SLOT:
  Use the default slot to change the trigger element (with a link, for example)
-->
<template>
  <div ref="Dropdown" class="Dropdown" :class="classes">
    <div class="Dropdown-trigger" @click="open">
      <slot>
        <lm-icon-button
          :class="iconClasses"
          :icon="icon"
          :outline="iconOutline"
          :tooltip="tooltip"
        />
      </slot>
    </div>
    <ul v-show="show" ref="DropdownMenu" class="Dropdown-menu">
      <li
        v-for="(item, index) in items"
        :key="index"
        :class="itemClasses(item)"
        @click="itemSelected(item, $event)"
      >
        <user-info v-if="item.user" :user="item.user" size="28px" />
        <template v-else>
          <lm-material-icon
            v-if="item.icon"
            :icon="item.icon"
            :outline="item.iconOutline"
          />
          <div v-if="item.checkbox" class="flex items-center">
            <lm-checkbox v-model="item.selected" />
            <span class="font-sans">{{ item.label }}</span>
          </div>
          <span v-else>{{ item.label }}</span>
        </template>
      </li>
    </ul>
  </div>
</template>

<script>
import LmCheckbox from '@/components/form/LmCheckbox'
import LmMaterialIcon from './LmMaterialIcon.vue'
import LmIconButton from './LmIconButton.vue'
import UserInfo from '@/components/landscape/topics/UserInfo'
import Popper from 'popper.js'

export default {
  name: 'LmDropdown',
  components: {
    LmCheckbox,
    UserInfo,
    LmIconButton,
    LmMaterialIcon
  },
  props: {
    items: {
      type: Array,
      required: true
    },
    id: {
      type: String,
      default: null
    },
    icon: {
      type: String,
      default: 'more_vert'
    },
    highlightIcon: {
      type: Boolean,
      default: false
    },
    iconOutline: {
      type: Boolean,
      default: false
    },
    iconColor: {
      type: String,
      default: 'black'
    },
    autoClose: {
      type: Boolean,
      default: false
    },
    tooltip: {
      type: String,
      default: null
    },
    selectedItem: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      show: false,
      init: false
    }
  },
  computed: {
    iconClasses() {
      return [
        'is-' + this.iconColor,
        {
          'is-highlight':
            this.highlightIcon ||
            this.nonDefaultOptionSelected ||
            this.isCheckboxSelected
        }
      ]
    },
    classes() {
      return [{'is-disabled': this.disabled}]
    },
    isCheckboxSelected() {
      return this.items.some(item => item.checkbox && item.selected)
    },
    nonDefaultOptionSelected() {
      return this.selectedItem && this.selectedItem !== this.items[0]
    },
    disabled() {
      return this.items.length === 0
    }
  },
  methods: {
    open() {
      if (!this.disabled) {
        window.addEventListener('click', this.close, false)
        this.show = true
        this.$emit('open')
        this.$nextTick(() => this.initPopper())
      }
    },
    close() {
      if (!this.init) {
        this.init = true
      } else {
        this.$emit('close')
        window.removeEventListener('click', this.close, false)
        this.show = false
        this.init = false
      }
    },
    initPopper() {
      if (!this.popper) {
        this.popper = new Popper(this.$refs.Dropdown, this.$refs.DropdownMenu, {
          placement: 'bottom-end',
          modifiers: {
            offset: {offset: '0, 5'},
            preventOverflow: {
              priority: ['left']
            }
          }
        })
      } else {
        this.popper.scheduleUpdate()
      }
    },
    itemClasses(item) {
      return [
        item === this.selectedItem ? 'is-selected' : '',
        item.title ? 'is-title' : '',
        item.checkbox ? 'is-checkbox' : '',
        item.disabled ? 'is-disabled' : '',
        item.customClass
      ]
    },
    itemSelected(item, e) {
      if (item.disabled) {
        e.stopImmediatePropagation()
        return
      }
      e.preventDefault() // Prevent bubble with checkbox, checkbox__label-text
      if (!this.autoClose) {
        e.stopImmediatePropagation()
      }
      if (item.checkbox) {
        item.selected = !item.selected // Change manually checkbox state
      }
      this.$emit('select', item, this.id)
    }
  }
}
</script>

<style>
.Dropdown {
  position: relative;
  height: 24px;
  &.is-disabled {
    pointer-events: none;
    cursor: default;
    opacity: 0.5;
  }
  .LmIconButton {
    &.is-black {
      color: theme('colors.gray.700');
    }
    &.is-white {
      color: white;
    }
    &.is-grey {
      color: theme('colors.gray.700');
      &:hover {
        color: white;
      }
    }
    &.is-highlight {
      color: theme('colors.highlight');
    }
    &:hover {
      color: theme('colors.gray.900');
    }
  }
  .Dropdown-menu {
    position: absolute;
    top: 30px;
    max-width: 500px;
    min-width: 190px;
    margin: 0;
    padding: 0;
    z-index: theme('zIndex.dropdown'); /* !? */
    background-color: white;
    border: 1px solid theme('colors.gray.300');
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
    /* Scroll */
    overflow-y: auto;
    max-height: calc(100vh - var(--h-header) - 150px);
    li {
      display: flex;
      align-items: center;
      margin: 0 !important;
      padding: 16px;
      border-bottom: 1px solid theme('colors.gray.300');
      color: theme('colors.gray.900');
      font-size: 14px;
      line-height: 1.2;
      @media (min-width: 360px) {
        white-space: nowrap;
      }
      .LmMaterialIcon {
        color: #555555;
        padding-right: 10px;
      }
      &.is-selected {
        color: theme('colors.highlight');
      }
      &:hover:not(.is-disabled) {
        background-color: theme('colors.gray.300');
        cursor: pointer;
      }
      &:last-child {
        border-bottom: none;
      }
      &.is-title {
        color: theme('colors.gray.500');
        border-bottom: none;
        pointer-events: none;
        cursor: default;
      }
      &.is-disabled {
        @apply text-gray-500 cursor-not-allowed;
        i {
          @apply text-gray-500;
        }
      }
    }
  }
}
</style>
